import React from 'react';
import { useNavigate } from 'react-router-dom';

const CheckIcon = () => (
  <svg className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
  </svg>
);

const LandingPage = () => {
  const navigate = useNavigate();

  const services = [
    {
      id: 'case-tracker',
      title: 'Case Tracker',
      subtitle: 'Know exactly where your case stands — right now',
      description: 'Stop refreshing the USCIS website. Get real-time status updates and email alerts the moment anything changes.',
      features: ['Live USCIS status sync', 'Full case history timeline', 'Instant email notifications'],
      icon: (
        <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15a2.25 2.25 0 012.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
        </svg>
      ),
      accentColor: 'bg-blue-600',
      badge: { text: 'Free account required', className: 'bg-slate-100 text-slate-600 border border-slate-200' },
      buttonText: 'Track My Case',
      buttonClassName: 'bg-blue-600 hover:bg-blue-700 text-white',
      onClick: () => navigate('/register')
    },
    {
      id: 'naturalization-test',
      title: 'Citizenship Test Prep',
      subtitle: 'Pass your naturalization interview with confidence',
      description: 'Study all 128 official civics questions the way that works best for you — flashcards, practice quizzes, or hands-free audio.',
      features: ['All 128 official civics questions', 'Interactive flashcards & quizzes', 'Hands-free audio study mode'],
      icon: (
        <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-7.007 11.55A5.981 5.981 0 006.75 15.75v-1.5" />
        </svg>
      ),
      accentColor: 'bg-emerald-600',
      badge: { text: '100% Free', className: 'bg-emerald-50 text-emerald-700 border border-emerald-200' },
      buttonText: 'Start Studying',
      buttonClassName: 'bg-emerald-600 hover:bg-emerald-700 text-white',
      onClick: () => navigate('/naturalization-test')
    },
    {
      id: 'legal-help',
      title: 'Find Legal Help',
      subtitle: 'Connect with the right immigration attorney for you',
      description: 'Search a directory of licensed immigration attorneys, filter by location, language, and practice area, and view their profiles on an interactive map.',
      features: ['Filter by location & language', 'Interactive map view', 'Practice area specializations'],
      icon: (
        <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v17.25m0 0c-1.472 0-2.882.265-4.185.75M12 20.25c1.472 0 2.882.265 4.185.75M18.75 4.97A48.416 48.416 0 0012 4.5c-2.291 0-4.545.16-6.75.47m13.5 0c1.01.143 2.01.317 3 .52m-3-.52l2.62 10.726c.122.499-.106 1.028-.589 1.202a5.988 5.988 0 01-2.031.352 5.988 5.988 0 01-2.031-.352c-.483-.174-.711-.703-.59-1.202L18.75 4.971zm-16.5.52c.99-.203 1.99-.377 3-.52m0 0l2.62 10.726c.122.499-.106 1.028-.589 1.202a5.989 5.989 0 01-2.031.352 5.989 5.989 0 01-2.031-.352c-.483-.174-.711-.703-.59-1.202L5.25 4.971z" />
        </svg>
      ),
      accentColor: 'bg-blue-600',
      badge: { text: '100% Free', className: 'bg-emerald-50 text-emerald-700 border border-emerald-200' },
      buttonText: 'Find a Lawyer',
      buttonClassName: 'bg-blue-600 hover:bg-blue-700 text-white',
      onClick: () => navigate('/find-legal-help')
    },
    {
      id: 'resources',
      title: 'Resources',
      subtitle: 'Every official USCIS tool, organized in one place',
      description: 'Stop hunting across government websites. Browse case status tools, processing times, forms, and official resources — all searchable in one place.',
      features: ['Processing time estimates', 'Official USCIS forms library', 'Searchable resource index'],
      icon: (
        <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0118 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
        </svg>
      ),
      accentColor: 'bg-slate-700',
      badge: { text: '100% Free', className: 'bg-emerald-50 text-emerald-700 border border-emerald-200' },
      buttonText: 'Browse Resources',
      buttonClassName: 'bg-slate-700 hover:bg-slate-800 text-white',
      onClick: () => navigate('/resources')
    }
  ];

  const testimonials = [
    {
      quote: "I used to check the USCIS website every single day. Now I just get an email when something changes. It's been such a relief.",
      name: 'Maria L.',
      detail: 'Green card applicant, Texas'
    },
    {
      quote: "The citizenship test prep is incredible. I passed my naturalization interview on the first try after studying with the audio mode during my commute.",
      name: 'Kwame A.',
      detail: 'Naturalized citizen, Georgia'
    },
    {
      quote: "Finding an immigration lawyer who spoke my language used to be so hard. I found one in 10 minutes using the attorney search.",
      name: 'Sofia R.',
      detail: 'Visa applicant, Florida'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:bg-white focus:text-navy-700 focus:px-4 focus:py-2 focus:rounded-lg focus:shadow-lg focus:font-semibold"
      >
        Skip to main content
      </a>

      {/* Header */}
      <header className="bg-gradient-to-r from-navy-700 to-blue-700 border-b border-blue-800 sticky top-0 z-40">
        <div className="container mx-auto px-4 py-3 flex justify-between items-center">
          {/* Logo */}
          <div className="flex items-center space-x-2.5 cursor-pointer group" onClick={() => navigate('/')}>
            <div className="relative w-9 h-9 flex-shrink-0">
              <svg className="w-9 h-9" viewBox="0 0 36 36" fill="none">
                <path d="M18 2L4 8v10c0 8.5 6 16 14 18 8-2 14-9.5 14-18V8L18 2z" fill="#1e3a5f" />
                <path d="M18 5L7 10v8.5C7 25.5 12 31.5 18 33c6-1.5 11-7.5 11-14.5V10L18 5z" fill="#2563eb" />
                <circle cx="12" cy="13" r="1.2" fill="white" opacity="0.9" />
                <circle cx="18" cy="11" r="1.2" fill="white" opacity="0.9" />
                <circle cx="24" cy="13" r="1.2" fill="white" opacity="0.9" />
                <circle cx="15" cy="18" r="1.2" fill="white" opacity="0.9" />
                <circle cx="21" cy="18" r="1.2" fill="white" opacity="0.9" />
                <path d="M13 23l3.5 3.5L24 19" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div>
              <span className="text-base font-bold leading-tight block text-white">My Immigration</span>
              <span className="text-xs font-semibold leading-tight block tracking-wide uppercase text-blue-200">Services</span>
            </div>
          </div>

          <nav aria-label="Primary" className="flex items-center space-x-1">
            <button
              onClick={() => navigate('/register')}
              className="hidden sm:flex items-center gap-1.5 text-blue-200 hover:text-white hover:bg-white/10 px-3 py-2 rounded-lg transition-colors text-sm font-semibold"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c.98 0 1.813.626 2.122 1.5m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z" />
              </svg>
              Case Tracker
            </button>
            <button onClick={() => navigate('/naturalization-test')} className="hidden sm:block text-slate-200 hover:text-white hover:bg-white/10 px-3 py-2 rounded-lg transition-colors text-sm font-medium">Citizenship Test</button>
            <button onClick={() => navigate('/find-legal-help')} className="hidden sm:block text-slate-200 hover:text-white hover:bg-white/10 px-3 py-2 rounded-lg transition-colors text-sm font-medium">Find Legal Help</button>
            <button onClick={() => navigate('/resources')} className="hidden sm:block text-slate-200 hover:text-white hover:bg-white/10 px-3 py-2 rounded-lg transition-colors text-sm font-medium">Resources</button>
            <div className="hidden sm:block w-px h-6 bg-white/20 mx-1"></div>
            <button onClick={() => navigate('/login')} className="text-slate-200 hover:text-white hover:bg-white/10 px-3 py-2 rounded-lg transition-colors text-sm font-medium">Login</button>
            <button onClick={() => navigate('/register')} className="bg-white hover:bg-slate-100 text-navy-700 px-4 py-2 rounded-lg transition-colors text-sm font-semibold">Get Started Free</button>
          </nav>
        </div>
      </header>

      <main id="main-content">

        {/* Hero Section */}
        <section className="py-20 bg-white border-b border-slate-100">
          <div className="container mx-auto px-4 text-center">
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-sm font-medium mb-6 border border-blue-200">
              <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
              </svg>
              Free to start — no credit card required
            </div>
            <h1 className="text-5xl sm:text-6xl font-bold text-navy-700 mb-5 leading-tight tracking-tight">
              Your immigration journey,<br />
              <span className="text-blue-600">simplified</span>
            </h1>
            <p className="text-xl text-slate-500 mb-10 max-w-2xl mx-auto leading-relaxed">
              Track your USCIS case status, prepare for your citizenship test,
              and find a trusted immigration attorney — all in one place.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-14">
              <button
                onClick={() => navigate('/register')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3.5 rounded-lg font-semibold text-lg transition-colors shadow-sm"
              >
                Get Started Free
              </button>
              <button
                onClick={() => navigate('/naturalization-test')}
                className="bg-white hover:bg-slate-50 text-navy-700 px-8 py-3.5 rounded-lg font-semibold text-lg transition-colors border border-slate-200"
              >
                Try Citizenship Test
              </button>
            </div>

            {/* Stats strip */}
            <div className="flex flex-col sm:flex-row gap-6 sm:gap-0 justify-center divide-y sm:divide-y-0 sm:divide-x divide-slate-200 border border-slate-200 rounded-xl bg-slate-50 max-w-2xl mx-auto px-6 py-4">
              <div className="flex-1 text-center sm:px-6">
                <div className="text-2xl font-bold text-navy-700">128</div>
                <div className="text-sm text-slate-500 mt-0.5">Official civics questions</div>
              </div>
              <div className="flex-1 text-center sm:px-6">
                <div className="text-2xl font-bold text-navy-700">4</div>
                <div className="text-sm text-slate-500 mt-0.5">Integrated tools</div>
              </div>
              <div className="flex-1 text-center sm:px-6">
                <div className="text-2xl font-bold text-emerald-600">Free</div>
                <div className="text-sm text-slate-500 mt-0.5">To get started</div>
              </div>
              <div className="flex-1 text-center sm:px-6">
                <div className="text-2xl font-bold text-navy-700">24/7</div>
                <div className="text-sm text-slate-500 mt-0.5">Real-time case updates</div>
              </div>
            </div>
          </div>
        </section>

        {/* Pain points strip */}
        <section className="py-10 bg-navy-700">
          <div className="container mx-auto px-4">
            <div className="grid sm:grid-cols-3 gap-6 max-w-4xl mx-auto text-center text-white">
              <div className="flex flex-col items-center gap-2">
                <svg className="w-6 h-6 text-blue-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm font-medium text-slate-200">Tired of refreshing USCIS to check your case?</p>
              </div>
              <div className="flex flex-col items-center gap-2">
                <svg className="w-6 h-6 text-blue-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
                </svg>
                <p className="text-sm font-medium text-slate-200">Not sure how to prepare for your citizenship interview?</p>
              </div>
              <div className="flex flex-col items-center gap-2">
                <svg className="w-6 h-6 text-blue-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                </svg>
                <p className="text-sm font-medium text-slate-200">Can't find an immigration attorney who speaks your language?</p>
              </div>
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-navy-700 mb-3">Everything you need, in one place</h2>
              <p className="text-lg text-slate-500 max-w-xl mx-auto">
                Four powerful tools covering every stage of your immigration journey
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
              {services.map((service) => (
                <div
                  key={service.id}
                  className="flex flex-col bg-white rounded-xl border border-slate-200 transition-all duration-200 overflow-hidden hover:shadow-lg hover:border-slate-300 hover:-translate-y-0.5"
                >
                  <div className={`h-1.5 w-full ${service.accentColor}`} />
                  <div className="p-6 flex flex-col flex-1">
                    <div className="w-12 h-12 rounded-lg bg-slate-100 text-navy-700 flex items-center justify-center mb-4">
                      {service.icon}
                    </div>

                    <div className="mb-1">
                      <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${service.badge.className}`}>
                        {service.badge.text}
                      </span>
                    </div>

                    <h3 className="text-lg font-bold text-navy-700 mt-2 mb-1">{service.title}</h3>
                    <p className="text-sm text-blue-600 font-medium mb-3">{service.subtitle}</p>

                    <p className="text-sm text-slate-500 mb-5 leading-relaxed">
                      {service.description}
                    </p>

                    <ul className="space-y-1.5 mb-6 flex-1">
                      {service.features.map((f) => (
                        <li key={f} className="flex items-start gap-2 text-sm text-slate-600">
                          <CheckIcon />
                          {f}
                        </li>
                      ))}
                    </ul>

                    <button
                      onClick={service.onClick}
                      className={`mt-auto w-full ${service.buttonClassName} py-2.5 px-4 rounded-lg transition-colors font-semibold text-sm`}
                    >
                      {service.buttonText}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="py-20 bg-white border-t border-b border-slate-100">
          <div className="container mx-auto px-4">
            <div className="text-center mb-14">
              <h2 className="text-3xl font-bold text-navy-700 mb-3">Up and running in minutes</h2>
              <p className="text-lg text-slate-500 max-w-xl mx-auto">No complicated setup. Just create an account and start.</p>
            </div>
            <div className="grid md:grid-cols-3 gap-10 max-w-3xl mx-auto">
              {[
                {
                  step: '1',
                  title: 'Create a free account',
                  desc: 'Sign up in seconds — just your email and a password. No credit card, no commitment.',
                  color: 'bg-blue-600',
                },
                {
                  step: '2',
                  title: 'Add your case number',
                  desc: 'Enter your USCIS receipt number and we\'ll pull your current case status instantly.',
                  color: 'bg-blue-600',
                },
                {
                  step: '3',
                  title: 'Get notified automatically',
                  desc: 'Relax. We\'ll email you the moment your case status changes — no more manual checking.',
                  color: 'bg-emerald-600',
                },
              ].map(({ step, title, desc, color }) => (
                <div key={step} className="text-center">
                  <div className={`w-12 h-12 ${color} rounded-full flex items-center justify-center mx-auto mb-5`}>
                    <span className="text-white text-lg font-bold">{step}</span>
                  </div>
                  <h3 className="text-base font-bold text-navy-700 mb-2">{title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-navy-700 mb-3">Trusted by immigrants like you</h2>
              <p className="text-lg text-slate-500 max-w-xl mx-auto">
                Real people navigating real immigration journeys
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {testimonials.map(({ quote, name, detail }) => (
                <div key={name} className="bg-white rounded-xl border border-slate-200 p-6 flex flex-col">
                  <svg className="w-7 h-7 text-blue-200 mb-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                  </svg>
                  <p className="text-slate-600 text-sm leading-relaxed flex-1 mb-5">"{quote}"</p>
                  <div>
                    <p className="text-sm font-bold text-navy-700">{name}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{detail}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Trust signals */}
        <section className="py-12 bg-white border-t border-b border-slate-100">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-4 gap-8 max-w-5xl mx-auto">
              {[
                {
                  icon: (
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
                    </svg>
                  ),
                  color: 'bg-blue-50 text-blue-600',
                  title: 'Real-Time USCIS Data',
                  desc: 'Status pulled directly from USCIS — always accurate, always current.',
                },
                {
                  icon: (
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                    </svg>
                  ),
                  color: 'bg-slate-100 text-slate-600',
                  title: 'Secure & Private',
                  desc: 'Your data is encrypted and never sold to third parties.',
                },
                {
                  icon: (
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z" />
                    </svg>
                  ),
                  color: 'bg-emerald-50 text-emerald-600',
                  title: 'Official Content Only',
                  desc: 'Civics questions sourced directly from the official USCIS study materials.',
                },
                {
                  icon: (
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                    </svg>
                  ),
                  color: 'bg-blue-50 text-blue-600',
                  title: 'Licensed Attorneys Only',
                  desc: 'Attorney directory lists licensed professionals — always verify credentials independently.',
                },
              ].map(({ icon, color, title, desc }) => (
                <div key={title} className="text-center">
                  <div className={`w-12 h-12 rounded-lg ${color} flex items-center justify-center mx-auto mb-4`}>
                    {icon}
                  </div>
                  <h3 className="text-sm font-bold text-navy-700 mb-1">{title}</h3>
                  <p className="text-xs text-slate-500 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="py-24">
          <div className="container mx-auto px-4 text-center">
            <div className="bg-navy-700 text-white rounded-2xl p-12 max-w-3xl mx-auto">
              <h2 className="text-4xl font-bold mb-4">Start your journey today</h2>
              <p className="text-lg text-slate-300 mb-3 max-w-lg mx-auto">
                Everything you need is free to access. Create an account in seconds.
              </p>
              <p className="text-sm text-slate-400 mb-10">No credit card required &bull; No hidden fees &bull; Cancel anytime</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={() => navigate('/register')}
                  className="bg-blue-500 hover:bg-blue-400 text-white px-8 py-3.5 rounded-lg font-bold text-lg transition-colors shadow-lg"
                >
                  Create Free Account
                </button>
                <button
                  onClick={() => navigate('/naturalization-test')}
                  className="bg-white/10 hover:bg-white/20 text-white border border-white/20 px-8 py-3.5 rounded-lg font-semibold text-lg transition-colors"
                >
                  Try Test Prep First
                </button>
              </div>
            </div>
          </div>
        </section>

      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 py-10">
        <div className="container mx-auto px-4 text-center">
          <div className="flex items-center justify-center space-x-2.5 mb-4">
            <div className="relative w-8 h-8 flex-shrink-0">
              <svg className="w-8 h-8" viewBox="0 0 36 36" fill="none">
                <path d="M18 2L4 8v10c0 8.5 6 16 14 18 8-2 14-9.5 14-18V8L18 2z" fill="#1e3a5f" />
                <path d="M18 5L7 10v8.5C7 25.5 12 31.5 18 33c6-1.5 11-7.5 11-14.5V10L18 5z" fill="#2563eb" />
                <circle cx="12" cy="13" r="1.2" fill="white" opacity="0.9" />
                <circle cx="18" cy="11" r="1.2" fill="white" opacity="0.9" />
                <circle cx="24" cy="13" r="1.2" fill="white" opacity="0.9" />
                <circle cx="15" cy="18" r="1.2" fill="white" opacity="0.9" />
                <circle cx="21" cy="18" r="1.2" fill="white" opacity="0.9" />
                <path d="M13 23l3.5 3.5L24 19" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="text-left">
              <span className="text-base font-bold text-navy-700 leading-tight block">My Immigration</span>
              <span className="text-xs font-semibold text-blue-600 leading-tight block tracking-wide uppercase">Services</span>
            </div>
          </div>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mb-5">
            <button onClick={() => navigate('/naturalization-test')} className="text-sm text-slate-500 hover:text-navy-700 transition-colors">Citizenship Test</button>
            <button onClick={() => navigate('/find-legal-help')} className="text-sm text-slate-500 hover:text-navy-700 transition-colors">Find Legal Help</button>
            <button onClick={() => navigate('/resources')} className="text-sm text-slate-500 hover:text-navy-700 transition-colors">Resources</button>
            <button onClick={() => navigate('/register')} className="text-sm text-slate-500 hover:text-navy-700 transition-colors">Sign Up</button>
            <button onClick={() => navigate('/login')} className="text-sm text-slate-500 hover:text-navy-700 transition-colors">Login</button>
          </div>
          <p className="text-slate-500 text-sm mb-3">
            Empowering your immigration journey with reliable tools and resources
          </p>
          <p className="text-xs text-slate-400 max-w-2xl mx-auto">
            Not affiliated with USCIS or any government agency. Not a lawyer referral service —
            we do not endorse or recommend any attorney; please verify credentials independently.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
